import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, ScrollView, Platform } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { DesktopTableFilters } from '@/components/common/admin/desktop/dataTable/DesktopTableFilters';
import { TasksFiltersHeaderDesktop } from './TasksFiltersHeaderDesktop';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ITaskItemAttributes, ITaskSortModel, ITaskFilterModel, TASK_STATUS_CONFIG, TaskStatusType } from '@/types/admin/tasks';
import { IColumnConfig, IFilterModel, ISortModel } from '@/types/admin/dataTable';
import { DesktopTaskDetailsPanel } from './DesktopTaskDetailsPanel';
import { InlineEmptyMessage } from '@/components';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { TaskRow } from '@/components/admin/tasks/TaskRow';
import { Accordion, QuickAddTask } from '@/components';
import { toDomainTaskFilterModel, toGenericTaskFilterModel, buildTaskTableColumns, hasActiveTaskFilters } from '@/utils/admin/tasks';

interface DesktopTasksViewProps {
  view: 'list' | 'archived';
  tasks: ITaskItemAttributes[];
  groupedTasks?: Record<string, ITaskItemAttributes[]>; // pass from hook to avoid regrouping
  searchQuery: string;
  onSearchChange: (q: string) => void;
  filterModel: ITaskFilterModel;
  onFilterChange: (filter: ITaskFilterModel) => void;
  sortModel: ITaskSortModel[];
  onSortChange: (sort: ITaskSortModel[]) => void;
  uniqueAssignees: string[];
  onCreateTask: () => void;
  onChangeView: (view: 'list' | 'archived') => void;
  onEditTask: (taskId: number) => void;
  onDuplicateTask?: (taskId: number) => void;
  onArchiveTask?: (taskId: number) => void;
  quickAddInputs: Record<string, string>;
  handleQuickCreateTask: (title: string, status: TaskStatusType) => Promise<void>;
  updateQuickAddInput: (status: TaskStatusType, text: string) => void;
  clearQuickAddInput: (status: TaskStatusType) => void;
  headerRight?: React.ReactNode;
}

export const DesktopTasksView: React.FC<DesktopTasksViewProps> = ({
  view,
  tasks,
  groupedTasks,
  searchQuery,
  onSearchChange,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  uniqueAssignees,
  onCreateTask,
  onChangeView,
  onEditTask,
  onDuplicateTask,
  onArchiveTask,
  quickAddInputs,
  handleQuickCreateTask,
  updateQuickAddInput,
  clearQuickAddInput,
  headerRight,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const isArchived = view === 'archived';
  const [forceOpenIncrement, setForceOpenIncrement] = useState(0);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    incoming: false,
    pending: false,
    todo: false,
    inProgress: false,
    completed: false,
    cancelled: false,
  });

  // Close all accordions on tab/view change
  useEffect(() => {
    setOpenSections({
      incoming: false,
      pending: false,
      todo: false,
      inProgress: false,
      completed: false,
      cancelled: false,
    });
  }, [view]);

  const hasActiveFilters = useMemo(() => hasActiveTaskFilters(filterModel as any, searchQuery, view), [searchQuery, filterModel, view]);

  useEffect(() => {
    if (hasActiveFilters) {
      setForceOpenIncrement((prev) => prev + 1);
    } else {
      setForceOpenIncrement(0);
    }
  }, [hasActiveFilters]);

  // Helpers: URL query synchronization (web only)
  const readQueryParams = useCallback(() => {
    if (Platform.OS !== 'web') return {} as { taskId?: number; form?: boolean };
    try {
      const usp = new URLSearchParams((globalThis as any).location?.search || '');
      const taskIdStr = usp.get('taskId');
      const formStr = usp.get('form');
      const taskId = taskIdStr ? Number.parseInt(taskIdStr) : undefined;
      const form = formStr === 'true';
      return { taskId: Number.isFinite(taskId as any) ? (taskId as number) : undefined, form };
    } catch {
      return {} as any;
    }
  }, []);

  const updateQueryParams = useCallback((params: Partial<{ taskId?: number | null; form?: boolean | null }>, method: 'push' | 'replace' = 'push') => {
    if (Platform.OS !== 'web') return;
    try {
      const loc = (globalThis as any).location;
      const hist = (globalThis as any).history;
      if (!loc || !hist) return;
      const usp = new URLSearchParams(loc.search || '');
      if ('taskId' in params) {
        const val = params.taskId;
        if (val == null) usp.delete('taskId'); else usp.set('taskId', String(val));
      }
      if ('form' in params) {
        const val = params.form;
        if (val == null || val === false) usp.delete('form'); else usp.set('form', 'true');
      }
      const next = `${loc.pathname}?${usp.toString()}`;
      if (method === 'replace') hist.replaceState(null, '', next); else hist.pushState(null, '', next);
    } catch {}
  }, []);

  // Init from URL on mount (web)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const { taskId, form } = readQueryParams();
    if (taskId) {
      setSelectedTaskId(taskId);
    }
    if (form) {
      // navigate to create task for parity with admin-web
      onCreateTask();
      // ensure URL is preserved; consumer screen handles back nav
    }
    // Listen to back/forward to restore panel state
    const onPop = () => {
      const { taskId: tid, form: fm } = readQueryParams();
      setSelectedTaskId(tid ?? null);
      if (fm) {
        onCreateTask();
      }
    };
    (globalThis as any).addEventListener?.('popstate', onPop);
    return () => (globalThis as any).removeEventListener?.('popstate', onPop);
  }, [onCreateTask, readQueryParams]);

  const openTaskDetails = useCallback((id: number) => {
    setSelectedTaskId(id);
    updateQueryParams({ taskId: id }, 'push');
  }, [updateQueryParams]);

  const closeTaskDetails = useCallback(() => {
    setSelectedTaskId(null);
    updateQueryParams({ taskId: null }, 'push');
  }, [updateQueryParams]);

  const openCreateTask = useCallback(() => {
    updateQueryParams({ form: true }, 'push');
    onCreateTask();
  }, [onCreateTask, updateQueryParams]);

  const grouped = useMemo(() => {
    if (groupedTasks) return groupedTasks;
    const base: Record<string, ITaskItemAttributes[]> = { incoming: [], pending: [], todo: [], inProgress: [], completed: [], cancelled: [] };
    tasks.forEach((t) => { const key = t.taskStatus as keyof typeof base; if (base[key]) base[key].push(t); });
    return base;
  }, [groupedTasks, tasks]);

  const columns: IColumnConfig<ITaskItemAttributes>[] = useMemo(() => buildTaskTableColumns(view, uniqueAssignees) as unknown as IColumnConfig<ITaskItemAttributes>[], [uniqueAssignees, view]);
  // Match admin-web: remove Status filter from desktop list view
  const desktopColumns: IColumnConfig<ITaskItemAttributes>[] = useMemo(() => {
    const cols = columns;
    if (view === 'list') {
      return cols.filter((c) => (c as any).key !== 'status');
    }
    return cols;
  }, [columns, view]);

  const renderTabs = () => (
    <View style={styles.tabs}>
      <TouchableOpacity
        style={[styles.tab, view === 'list' && styles.tabActive]}
        onPress={() => onChangeView('list')}
        activeOpacity={0.8}
      >
        <Text style={[styles.tabText, view === 'list' && styles.tabTextActive]}>Tasks</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.tab, view === 'archived' && styles.tabActive]}
        onPress={() => onChangeView('archived')}
        activeOpacity={0.8}
      >
        <Text style={[styles.tabText, view === 'archived' && styles.tabTextActive]}>Archived</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeader = () => (
    <View style={styles.headerArea}>
      {renderTabs()}
      <TasksFiltersHeaderDesktop
        columns={desktopColumns}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        filterModel={toGenericTaskFilterModel(filterModel as any)}
        onFilterChange={(m) => onFilterChange(toDomainTaskFilterModel(m, filterModel as any) as any)}
        sortModel={sortModel as unknown as ISortModel[]}
        onSortChange={(s) => onSortChange(s as unknown as ITaskSortModel[])}
        headerRight={headerRight}
        showClearFilters={!!filterModel.assignedTo?.length || !!filterModel.creator?.length}
        onClearFilters={() => onFilterChange({ ...filterModel, assignedTo: undefined, creator: [] })}
      />
    </View>
  );

  const keyExtractor = (item: ITaskItemAttributes) => String(item.id);

  const renderItem = useCallback(({ item }: { item: ITaskItemAttributes }) => (
    <TaskRow
      task={item}
      onShowDetails={(id) => openTaskDetails(id)}
      onEdit={(id) => onEditTask(id)}
      onDuplicate={onDuplicateTask}
      onArchive={onArchiveTask}
      isArchived={isArchived}
      variant="desktop"
    />
  ), [isArchived, onEditTask, onDuplicateTask, onArchiveTask, openTaskDetails]);

  const SectionHeader = ({ icon, label, count }: { icon?: React.ReactNode; label: string; count: number }) => (
    <View style={styles.accordionHeader}>
      <View style={styles.headerLeft}>
        {icon}
        <Text style={styles.headerTitle}>{label}</Text>
        <Text style={styles.headerCount}>{count}</Text>
      </View>
    </View>
  );

  const StatusAccordion = ({
    keyName,
    label,
    color,
    icon,
    items,
    quickAdd,
  }: {
    keyName: keyof typeof grouped;
    label: string;
    color?: string;
    icon?: React.ReactNode;
    items: ITaskItemAttributes[];
    quickAdd?: React.ReactNode;
  }) => {
    const count = items.length;
    const headerIcon = icon || (
      <View style={[styles.statusDot, { backgroundColor: color || ADMIN_COLORS.TEXT_SECONDARY }]} />
    );
    const forceOpen = count > 0 ? forceOpenIncrement : undefined;
    return (
      <Accordion
        forceOpen={forceOpen}
        defaultOpen={!!openSections[keyName]}
        onToggle={(isOpen) => setOpenSections((prev) => ({ ...prev, [keyName]: isOpen }))}
        header={<SectionHeader icon={headerIcon} label={label} count={count} />}
      >
        {items.map((t) => (
          <TaskRow
            key={`${keyName}-${t.id}`}
            task={t}
            onShowDetails={(id) => setSelectedTaskId(id)}
            onEdit={(id) => onEditTask(id)}
            onDuplicate={onDuplicateTask}
            onArchive={onArchiveTask}
            isArchived={isArchived}
            variant="desktop"
          />
        ))}
        {count === 0 && keyName === 'incoming' && (
          <View style={styles.emptySection}>
            <Text style={styles.emptyText}>Incoming requests from residents will appear here</Text>
          </View>
        )}
        {quickAdd}
      </Accordion>
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Tasks"
        subtitle=''
        showAddButton={true}
        addButtonText="New Task"
        onAddPress={openCreateTask}
      />

      {renderHeader()}

      <View style={styles.listContainer}>
        {isArchived ? (
          tasks.length === 0 ? (
            <View style={{ flex: 1, paddingHorizontal: 16, paddingTop: 12 }}>
              {(() => {
                const monthYear = (filterModel.month && filterModel.year)
                  ? new Date(filterModel.year!, filterModel.month! - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
                  : undefined;
                const cfg = monthYear
                  ? EMPTY_MESSAGES.tasks.archivedForMonth(monthYear)
                  : (hasActiveFilters ? EMPTY_MESSAGES.tasks.archivedWithFilters : EMPTY_MESSAGES.tasks.archivedDefault);
                return (
                  <InlineEmptyMessage
                    title={cfg.title}
                    message={cfg.message}
                    icon={cfg.icon}
                    containerStyle={styles.emptyMessageContainer}
                    titleStyle={styles.emptyMessageText}
                    messageStyle={styles.emptyMessageSubtext}
                  />
                );
              })()}
            </View>
          ) : (
            <FlatList
              data={tasks}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              initialNumToRender={20}
              windowSize={8}
              removeClippedSubviews
              showsVerticalScrollIndicator={false}
            />
          )
        ) : (
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {[
              { key: 'incoming', label: TASK_STATUS_CONFIG.incoming.label, icon: <Text style={styles.emoji}>🏠</Text>, color: undefined },
              { key: 'pending', label: TASK_STATUS_CONFIG.pending.label, color: ADMIN_COLORS.TEXT_SECONDARY, quickAddStatus: 'pending' as TaskStatusType },
              { key: 'todo', label: TASK_STATUS_CONFIG.todo.label, color: '#3B82F6', quickAddStatus: 'todo' as TaskStatusType },
              { key: 'inProgress', label: TASK_STATUS_CONFIG.inProgress.label, color: '#F59E0B', quickAddStatus: 'inProgress' as TaskStatusType },
              { key: 'completed', label: TASK_STATUS_CONFIG.completed.label, color: '#10B981', quickAddStatus: 'completed' as TaskStatusType },
              { key: 'cancelled', label: TASK_STATUS_CONFIG.cancelled.label, icon: <SVG_COMPONENTS.X_CIRCLE width={16} height={16} color="#EF4444" /> },
            ].map((cfg) => (
              <StatusAccordion
                key={cfg.key}
                keyName={cfg.key as keyof typeof grouped}
                label={cfg.label}
                color={cfg.color}
                icon={cfg.icon}
                items={(grouped as any)[cfg.key] as ITaskItemAttributes[]}
                quickAdd={cfg.quickAddStatus ? (
                  <QuickAddTask
                    taskStatus={cfg.quickAddStatus}
                    value={quickAddInputs[cfg.key as keyof typeof quickAddInputs] || ''}
                    onValueChange={(v) => updateQuickAddInput(cfg.quickAddStatus!, v)}
                    onSubmit={handleQuickCreateTask}
                    onClear={() => clearQuickAddInput(cfg.quickAddStatus!)}
                  />
                ) : undefined}
              />
            ))}
          </ScrollView>
        )}
      </View>

      <DesktopTaskDetailsPanel
        taskId={selectedTaskId}
        visible={selectedTaskId != null}
        onClose={closeTaskDetails}
        onEdit={(id) => { onEditTask(id); closeTaskDetails(); }}
      />

    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  headerArea: {
    // borderBottomWidth: 1,
    // borderBottomColor: ADMIN_COLORS.BORDER,
  },
  tabs: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    padding: 4,
    marginBottom: 12,
    marginHorizontal: 16,
    marginTop: 4,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 6,
  },
  tabActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  tabText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '500',
  },
  tabTextActive: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  listContainer: {
    flex: 1,
    marginTop: 16,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  emoji: {
    fontSize: 18,
  },
  statusDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '400',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  headerCount: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  emptySection: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 380,
  },
});


